/**
 * The narrow-screen shell.
 *
 * One thing on screen at a time, chosen from a bottom tab bar, over exactly the
 * state the desktop shell uses — the same `UserWorkspace`, the same reducer, the
 * same `ThemeProvider`. The fork is in `App`, below `usePersistentWorkspace`, so
 * rotating a tablet across the breakpoint swaps a tree rather than reloading a
 * workspace. See [mobile.md](../../../../docs/features/mobile.md).
 *
 * ## Inactive views are hidden, not unmounted, and never `display: none`
 *
 * Two different failures sit behind that one rule.
 *
 * `display: none` gives a subtree **no geometry**, so a chart coming back from
 * another tab measures a zero-height container and lays out wrong — the trap
 * `charts.md` documents for the div SciChart mutates on `delete()`, arriving by
 * another route. `visibility: hidden` keeps the box.
 *
 * Unmounting is worse still: `ScreenerView` owns the screener socket and each
 * chart owns a WebGL surface, so a tab switch would become a reconnect plus a
 * teardown, and every visit to the List would re-request the whole screen.
 *
 * ## What is *not* here
 *
 * The vertical strip's contents — chart type, indicators, drawing tools — have
 * no home yet. They belong in a sheet behind the toolbar's `⋯`, which the mock
 * shows and this does not yet implement; the shell is the part that had to be
 * right first.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import {
  mintUlid,
  type ChartGroup,
  type Execution,
  type Plan,
  type Theme,
  type Trade,
  type UserWorkspace,
} from "@qufolio/model";

import type { WorkspaceAction } from "../lib/workspace-actions";
import { chartGroupConfig, screenerConfig } from "../lib/section-config";
import { chartGrid } from "../lib/chart-grid";
import { briefSection, layoutSummary } from "../lib/mobile-brief";
import { bucketsBriefConfig } from "../lib/buckets-brief";
import { briefBoardConfig, isMemberCopy } from "../lib/brief-board";
import { BucketsBriefView } from "./BucketsBriefView";
import { BriefBoardView } from "./BriefBoardView";
import { TABLET_QUERY } from "../lib/use-narrow";
import { nextIndex, prevIndex, canStep, swipeStep } from "../lib/chart-pager";
import { fetchJournal, journalEnabled } from "../api/journal";
import { journalAnnotations as journalAnnotationsFor } from "../lib/plan-annotations";
import { ChartPager } from "./ChartPager";
import type { GoToDate } from "./ChartGroupView";
import { GoToDateBox, VisibleBarsBox } from "./ChartToolbar";
import { IntervalPicker } from "./IntervalPicker";
import { IndicatorButton } from "./IndicatorPicker";
import { emptyCalendar, fetchMarketCalendar, type MarketCalendar } from "../api/calendar";
import { visibleBarsOf } from "../lib/chart-window";
import { MobileRows } from "./MobileRows";
import { MobileMore } from "./MobileMore";
import { MobileTabBar, type MobileTab } from "./MobileTabBar";
import { BRAND } from "../lib/brand-current";
import { ScreenerView } from "./ScreenerView";
import { SymbolGrip } from "./SymbolGrip";
import type { WheelSymbol } from "./SymbolWheel";
import { SymbolDialog } from "./SymbolDialog";
import { ThemeEditor } from "./ThemeEditor";
import { SymbolDataTabs } from "./SymbolDataPanel";
import { HoldingsCard } from "./HoldingsCard";
import { FinStCard } from "./symbol-views";
import type { DataTab } from "../lib/pip";
import { TradesView } from "./TradesView";
import { CommunityPanel } from "./community/CommunityPanel";
import { ChatPanel } from "./chat/ChatPanel";
import { chatConfigured } from "../api/chat";
import { cachedCatalog } from "../api/indicators";
import { actionById, clientTools, type ActionContext } from "../lib/actions";
import { chatGate, workspaceContext } from "../lib/chat";
import { useAvailable } from "../lib/features";
import { adoptLayout } from "../lib/layout-adopt";
import type { TradeGroup } from "../lib/trade-groups";
import type { SaveStatus } from "../api/persistence";

export interface MobileAppProps {
  workspace: UserWorkspace;
  dispatch: Dispatch<WorkspaceAction>;
  themes: Theme[];
  onSaveTheme: (theme: Theme) => void;
  /** Which layout is on screen — owned by `App`, shared with the desktop shell. */
  layoutIndex: number;
  setLayoutIndex: Dispatch<SetStateAction<number>>;
  /** `loading` holds the charting views back — see the guard in the component. */
  saveStatus: SaveStatus;
}

/**
 * Fills whatever the host gives it, rather than claiming the viewport.
 *
 * It was `position: fixed; inset: 0`, which covered the acting-as banner
 * `App` renders above it — so on a phone an admin could be reading somebody
 * else's journal with nothing on screen saying so. That banner exists
 * *precisely* because that state must be impossible to forget, and a shell that
 * paints over it is worse than one that never had it.
 *
 * The host now owns the fixed box and this is a flex child of it, so the banner
 * takes its row and the shell takes the rest.
 */
const SHELL: React.CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  background: "var(--qf-background)",
};

const VIEWS: React.CSSProperties = { flex: "1 1 auto", minHeight: 0, position: "relative" };

/**
 * The chart's bottom row (owner, 2026-10-13; `docs/mockups/form-factors.html`
 * §1): ‹ 22 · grip ~66 · bars 44 · date 32 · interval ~48 · fx 32 · ⋯ 32 · › 22,
 * seven 8px gaps and 20px of padding — ~354px of a 390px phone. Below this
 * width (iPhone SE / mini) the gaps close to 6px. No price: it is on the
 * chart's axis tag. The symbol takes what is left (see `SymbolGrip`).
 */
const WIDE_GAPS_PX = 380;

const ARROW: React.CSSProperties = {
  flex: "0 0 auto",
  width: 22,
  height: 28,
  fontSize: 18,
  lineHeight: "24px",
  padding: 0,
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  color: "var(--qf-text-secondary)",
};

/** fx and ⋯: the same 32px key as the date's calendar. */
const KEY: React.CSSProperties = {
  flex: "0 0 auto",
  width: 32,
  height: 28,
  padding: 0,
  font: "inherit",
  fontSize: 13,
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  color: "var(--qf-text-secondary)",
};

function useMinWidth(px: number): boolean {
  return useMedia(`(min-width: ${px}px)`);
}

function useMedia(query: string): boolean {
  const read = () => typeof window !== "undefined" && !!window.matchMedia?.(query).matches;
  const [on, setOn] = useState(read);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const change = () => setOn(mq.matches);
    mq.addEventListener("change", change);
    return () => mq.removeEventListener("change", change);
  }, [query]);
  return on;
}

/**
 * The ⋯ key's list: the same layouts as More › Layouts, one tap from the chart.
 * Sits above the bottom row at the right, over the chart; a tap anywhere else
 * closes it.
 */
function LayoutList({
  layouts,
  current,
  onPick,
  onClose,
}: {
  layouts: readonly { id: string; name?: string; sections: readonly { kind: string }[] }[];
  current: number;
  onPick: (index: number) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, zIndex: 30 }} />
      <div
        role="menu"
        aria-label="Layouts"
        style={{
          position: "absolute",
          right: 8,
          bottom: 52,
          zIndex: 31,
          minWidth: 220,
          maxHeight: "60%",
          overflowY: "auto",
          background: "var(--qf-surface)",
          border: "1px solid var(--qf-border)",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        }}
      >
        {layouts.map((layout, i) => {
          return (
            <button
              key={layout.id}
              type="button"
              role="menuitemradio"
              aria-checked={i === current}
              onClick={() => onPick(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "10px 12px",
                font: "inherit",
                fontSize: 14,
                textAlign: "left",
                border: 0,
                borderTop: i ? "1px solid var(--qf-border)" : 0,
                background: "transparent",
                color: "var(--qf-text)",
              }}
            >
              <span style={{ width: 14, color: "var(--qf-accent)" }}>{i === current ? "✓" : ""}</span>
              <span style={{ flex: 1 }}>{layout.name || "Untitled"}</span>
              <span style={{ fontSize: 12, color: "var(--qf-text-muted)" }}>
                {layoutSummary(layout)}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/** The data views rebuilt for a phone's width; the rest render as they are. */
const PHONE_CARDS = { phone: { holdings: HoldingsCard, fundamentals: FinStCard } };

/** Where a tablet remembers All or Full. */
const CHART_MODE_KEY = "qf.tablet.chartMode";

function readChartMode(): "all" | "full" {
  try {
    return localStorage.getItem(CHART_MODE_KEY) === "full" ? "full" : "all";
  } catch {
    return "all";
  }
}

/** One view's box. Always laid out; only its visibility changes — see the header. */
function viewStyle(on: boolean): React.CSSProperties {
  return {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    visibility: on ? "visible" : "hidden",
    // A hidden view still has a box, so without this its buttons would still be
    // under the finger of anyone tapping the visible one.
    pointerEvents: on ? "auto" : "none",
  };
}

export function MobileApp({
  workspace,
  dispatch,
  themes,
  onSaveTheme,
  saveStatus,
  layoutIndex,
  setLayoutIndex,
}: MobileAppProps) {
  const [tab, setTab] = useState<MobileTab>("charts");
  const [chartIndex, setChartIndex] = useState(0);
  /** Which way the last chart change travelled, so the slide matches the tap. */
  const [direction, setDirection] = useState<1 | -1>(1);
  const [themeOpen, setThemeOpen] = useState(false);
  const [tradeGroup, setTradeGroup] = useState<TradeGroup>("plan");
  /** The Data tab's view — held across symbol changes, like the desktop panel's. */
  const [dataTab, setDataTab] = useState<DataTab>("profile");

  /** The list as shown, lifted out of the screener for the symbol wheel. */
  const [symbols, setSymbols] = useState<WheelSymbol[]>([]);
  /** The wheel itself, rendered into the chart stage rather than the toolbar. */
  const [wheel, setWheel] = useState<ReactNode>(null);
  /** The search dialog, and which chart its answer is for. */
  const [search, setSearch] = useState<{ chartGroupId: string; keeps?: string; query?: string } | null>(null);

  /**
   * The bottom row's go-to-date box: the calendar it snaps against, and the
   * request it hands the pager. Fetched here rather than threaded from `App`,
   * whose calendar lives in the desktop shell's component.
   */
  const [calendar, setCalendar] = useState<MarketCalendar>(emptyCalendar);
  useEffect(() => {
    void fetchMarketCalendar().then(setCalendar);
  }, []);
  const [goTo, setGoTo] = useState<(GoToDate & { chartGroupId: string }) | undefined>();
  const wideGaps = useMinWidth(WIDE_GAPS_PX);
  /**
   * A phone on its side: the tabs become a rail on the left (native-apps.md
   * §9.2). The shell's own box turns from a column into a row, the rail draws
   * first, and the views take the right and bottom insets the bar used to.
   */
  const landscape = useMedia("(orientation: landscape)");
  /**
   * A tablet: the Chart view has All and Full (native-apps.md §9.3–9.4). The
   * mode is the one used last on this device — view state (D8), so it lives in
   * `localStorage`, and storage that throws just means starting in All.
   */
  const tablet = useMedia(TABLET_QUERY);
  const [chartMode, setChartMode] = useState<"all" | "full">(readChartMode);
  const changeChartMode = (mode: "all" | "full") => {
    setChartMode(mode);
    try {
      localStorage.setItem(CHART_MODE_KEY, mode);
    } catch {
      // Private window or blocked storage: the mode still changes for now.
    }
  };
  const showAll = tablet && chartMode === "all";
  const shell: React.CSSProperties = landscape ? { ...SHELL, flexDirection: "row" } : SHELL;
  const views: React.CSSProperties = landscape
    ? {
        ...VIEWS,
        // Margins, not padding: every view is `position: absolute; inset: 0`,
        // which fills the padding box and would run under the insets anyway.
        marginRight: "env(safe-area-inset-right)",
        marginBottom: "env(safe-area-inset-bottom)",
      }
    : VIEWS;
  /** The ⋯ key's layout list. */
  const [layoutsOpen, setLayoutsOpen] = useState(false);
  /** Where a swipe along the bottom row started. */
  const swipeFrom = useRef<{ x: number; y: number } | null>(null);

  const index = Math.min(layoutIndex, workspace.layouts.length - 1);
  const layout = workspace.layouts[index];

  /** Every chart in this layout, in the order the layout lists them, with its section. */
  const charts = useMemo(
    () =>
      (layout?.sections ?? [])
        .filter((s) => s.kind === "chartGroup")
        .map((section) => ({ section, group: chartGroupConfig(section) }))
        .filter((c): c is { section: typeof c.section; group: ChartGroup } => !!c.group),
    [layout],
  );
  const groups = useMemo<ChartGroup[]>(() => charts.map((c) => c.group), [charts]);

  /**
   * The iPad's All mode: every chart in the layout's own arrangement, the same
   * mounted pages laid out on its grid (`lib/chart-grid.ts`). Cells line up with
   * `groups`, which is what the pager indexes.
   */
  const allGrid = useMemo(() => {
    if (!layout) return undefined;
    const grid = chartGrid(layout);
    return { rows: grid.rows, columns: grid.columns, cells: charts.map((c) => grid.cells.get(c.section.id)) };
  }, [layout, charts]);

  // A layout edited elsewhere can lose the chart this shell is sitting on, and
  // an index past the end renders nothing at all — the same clamp `layoutIndex`
  // gets, for the same reason.
  const chartAt = groups.length === 0 ? 0 : Math.min(chartIndex, groups.length - 1);
  const group = groups[chartAt];

  const screenerSection = layout?.sections.find((s) => s.kind === "screener");
  const screener = screenerSection && screenerConfig(screenerSection);

  /**
   * A Buckets Brief or Daily Brief layout: the Chart tab shows it as cards
   * instead of the chart pager (`docs/features/mobile.md`, "The briefs are
   * cards too"). Read-only — no `onChange` reaches the board.
   */
  const brief = briefSection(layout);
  const buckets = brief && bucketsBriefConfig(brief);
  const board = brief && briefBoardConfig(brief);

  /** Last price per symbol, accumulated as charts load — the grip's readout. */
  const [prices, setPrices] = useState<ReadonlyMap<string, number>>(new Map());
  const [barTimes, setBarTimes] = useState<ReadonlyMap<string, number>>(new Map());
  const rememberPrice = useCallback((symbol: string, price: number, at: number) => {
    const key = symbol.toUpperCase();
    setPrices((was) => (was.get(key) === price ? was : new Map(was).set(key, price)));
    setBarTimes((was) => (was.get(key) === at ? was : new Map(was).set(key, at)));
  }, []);

  /** The journal, fetched once — one document, however many views read it. */
  const [journal, setJournal] = useState<{
    plans: Plan[];
    trades: Trade[];
    executions: Execution[];
  }>({ plans: [], trades: [], executions: [] });
  useEffect(() => {
    if (!journalEnabled()) return;
    let cancelled = false;
    fetchJournal()
      .then((loaded) => {
        if (cancelled) return;
        setJournal({
          plans: loaded.plans ?? [],
          trades: loaded.trades ?? [],
          executions: loaded.executions ?? [],
        });
      })
      .catch(() => {
        if (!cancelled) setJournal({ plans: [], trades: [], executions: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** The plan's lines for the chart on screen. Derived, never stored. */
  const journalLines = useMemo(
    () =>
      group?.symbol
        ? journalAnnotationsFor(
            journal.plans,
            journal.trades,
            journal.executions,
            group.symbol,
            barTimes.get(group.symbol.toUpperCase()),
          )
        : { annotations: [], editable: new Set<string>() },
    [journal, group?.symbol, barTimes],
  );

  /** Step to the next (1) or previous (-1) chart, wrapping. */
  const step = useCallback(
    (dir: 1 | -1) => {
      if (!canStep(groups.length)) return;
      setDirection(dir);
      setChartIndex((was) => (dir === 1 ? nextIndex : prevIndex)(groups.length, was));
    },
    [groups.length],
  );
  /** The Chart tab's own second tap. */
  const cycle = useCallback(() => step(1), [step]);

  const setSymbol = useCallback(
    (chartGroupId: string, symbol: string) => {
      dispatch({ type: "setSymbol", chartGroupId, symbol });
    },
    [dispatch],
  );

  /**
   * The AI tab (docs/features/chat.md) — a row in More, full screen, the
   * desktop's panel. Both gates plus a quevee, and the brand's `more` slot.
   *
   * **Mounted on first open, then kept.** Mounting opens a socket to quevee,
   * which a phone that never asks anything should not pay for; once opened it
   * stays mounted, as Community does, so switching tabs mid-answer does not
   * cut the answer off.
   */
  const chatOn = chatGate("mobile", chatConfigured(), useAvailable("agent")).panel;
  const [aiOpened, setAiOpened] = useState(false);
  useEffect(() => {
    if (tab === "agent") setAiOpened(true);
  }, [tab]);

  /**
   * The registry's view of the phone — the part of the desktop's
   * `actionContext` this shell has. The command search's additions are
   * absent, so those actions refuse with their own reasons, which the agent
   * reads and reports.
   */
  const actionContext = useMemo<ActionContext>(
    () => ({
      activeChartGroupId: group?.id,
      chartIds: groups.map((g) => g.id),
      chartGroup: (id) => groups.find((g) => g.id === id),
      dispatch,
      mintId: mintUlid,
      indicators: cachedCatalog,
      dataPanel: {
        openOn: (_chartGroupId, next) => {
          if (next) setDataTab(next);
          setTab("data");
        },
        setTab: setDataTab,
        close: () => setTab("charts"),
        // One window on a phone: there is nowhere to dock to or detach from.
        dock: () => undefined,
        detach: () => undefined,
      },
    }),
    [group?.id, groups, dispatch],
  );
  const actionContextRef = useRef(actionContext);
  actionContextRef.current = actionContext;

  /**
   * Nothing that charts or screens, until the load answers.
   *
   * The desktop shell holds its grid back at the same moment and for the same
   * reason — see the comment on `SectionGrid` in `App`. `usePersistentWorkspace`
   * seeds the reducer with the demo workspace, so on an account that has saved,
   * the tree mounted here charts a symbol nobody asked for and requests a
   * screener snapshot nobody asked for; and because every id in that fixture is
   * freshly minted, `reset` matches none of them and the pager tears its WebGL
   * surface down and rebuilds it. On a phone that is the whole screen blinking.
   *
   * **Only `loading`.** `error` still renders: the demo workspace is then the
   * only thing there is, and `api/persistence` already refuses every write.
   * Unconfigured builds report `off` and are untouched.
   */
  if (saveStatus === "loading") {
    return (
      <div style={shell}>
        <div style={views} />
        {/* The bar stays. Which tabs exist does not depend on which workspace
            loaded, and a phone showing nothing at all reads as a crash rather
            than as a wait. Its counts come from the same values the real return
            passes, so no number moves when the views arrive. */}
        <MobileTabBar
          active={tab}
          onSelect={setTab}
          onCycle={cycle}
          chartCount={groups.length}
          chartIndex={chartAt}
          rail={landscape}
        />
      </div>
    );
  }

  return (
    <div style={shell}>
      <div style={views}>
        {/* ── Screener ──────────────────────────────────────────────────── */}
        {/* **Not rendered at all when the brand does not have it**, rather than
            rendered and never shown. `viewStyle` keeps every view mounted on
            purpose — a chart that unmounted would lose its WebGL surface and its
            socket on every tab change — but that argument is about a view you
            can get back to. A screener no tab reaches would sit here holding an
            open socket for a feature this app does not have, which is the
            opposite of what "off means gone" is for. */}
        {BRAND.enabled("screener") && (
        <div style={viewStyle(tab === "screener")}>
          {screener && screenerSection ? (
            <ScreenerView
              config={screener}
              onConfigChange={(patch) =>
                dispatch({ type: "updateScreener", sectionId: screenerSection.id, patch })
              }
              onSavedFiltersChange={(savedFilters) =>
                dispatch({
                  type: "updateScreener",
                  sectionId: screenerSection.id,
                  patch: { savedFilters },
                })
              }
              // Picking a row charts it and takes you to the chart, which is
              // the reason the two tabs are next to each other.
              onSelectSymbol={
                group
                  ? (symbol) => {
                      setSymbol(group.id, symbol);
                      setTab("charts");
                    }
                  : undefined
              }
              renderRows={({ frames, config, meta, onSelectSymbol, query, onRowCounts }) => (
                <MobileRows
                  frames={frames}
                  config={config}
                  meta={meta}
                  onSelectSymbol={onSelectSymbol}
                  chartedSymbol={group?.symbol}
                  onSymbols={setSymbols}
                  query={query}
                  onRowCounts={onRowCounts}
                  // An iPad's row has room for six figures a line.
                  wide={tablet}
                  // Not in this list: the full symbol search, charting the pick.
                  onSearchAll={group ? (q) => setSearch({ chartGroupId: group.id, keeps: group.symbol, query: q }) : undefined}
                />
              )}
            />
          ) : (
            <Notice>This layout has no screener.</Notice>
          )}
        </div>
        )}

        {/* ── Chart ─────────────────────────────────────────────────────── */}
        <div style={viewStyle(tab === "charts")}>
          {brief ? (
            <div style={{ flex: "1 1 auto", minHeight: 0 }}>
              {buckets ? (
                <BucketsBriefView config={buckets} cards />
              ) : board && layout ? (
                <BriefBoardView
                  config={board}
                  member={isMemberCopy(layout, false)}
                  sourceVersion={layout.source?.version}
                  cards
                />
              ) : null}
            </div>
          ) : (
          <ChartPager
            groups={groups}
            layoutId={layout?.id ?? ""}
            index={chartAt}
            all={showAll ? allGrid : undefined}
            onSelect={setChartIndex}
            direction={direction}
            overlay={wheel}
            goTo={goTo}
            journal={
              group
                ? {
                    chartGroupId: group.id,
                    annotations: journalLines.annotations,
                    editable: journalLines.editable,
                  }
                : undefined
            }
            onLastPrice={rememberPrice}
            onPaneHeights={(chartGroupId, heights) =>
              dispatch({ type: "setPaneHeights", chartGroupId, heights })
            }
            onWindow={(chartGroupId, window) =>
              dispatch({ type: "setBarWindow", chartGroupId, ...window })
            }
          />
          )}
          {brief && (
            // No chart, so no symbol, interval or fx: only the way to another layout.
            <div
              style={{
                flex: "0 0 auto",
                display: "flex",
                justifyContent: "flex-end",
                padding: "6px 10px",
                background: "var(--qf-surface)",
                borderTop: "1px solid var(--qf-border)",
              }}
            >
              <button
                type="button"
                aria-label="Layouts"
                aria-expanded={layoutsOpen}
                onClick={() => setLayoutsOpen((v) => !v)}
                style={KEY}
              >
                ⋯
              </button>
            </div>
          )}
          {group && !brief && (
            <div
              style={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                gap: wideGaps ? 8 : 6,
                padding: "6px 10px",
                background: "var(--qf-surface)",
                borderTop: "1px solid var(--qf-border)",
                // The row's sideways swipe is ours; the grip claims its own
                // vertical drag the same way.
                touchAction: "none",
              }}
              // A sideways swipe along the row pages between the layout's charts,
              // wrapping. Not over the canvas — a drag there is a pan — and not
              // from a box, where a drag selects text.
              onPointerDown={(e) => {
                // React bubbles a portal's events here too — the fx sheet is
                // one — so only a press inside the row's own DOM is a swipe.
                const inRow = e.currentTarget.contains(e.target as Node);
                swipeFrom.current =
                  !inRow || e.target instanceof HTMLInputElement ? null : { x: e.clientX, y: e.clientY };
              }}
              onPointerUp={(e) => {
                const from = swipeFrom.current;
                swipeFrom.current = null;
                if (from) {
                  const dir = swipeStep(e.clientX - from.x, e.clientY - from.y);
                  if (dir) step(dir);
                }
              }}
              onPointerCancel={() => {
                swipeFrom.current = null;
              }}
            >
              {canStep(groups.length) && (
                <button type="button" aria-label="Previous chart" onClick={() => step(-1)} style={ARROW}>
                  ‹
                </button>
              )}
              <SymbolGrip
                symbol={group.symbol}
                symbols={symbols}
                onPick={(symbol) => setSymbol(group.id, symbol)}
                onSearch={() => setSearch({ chartGroupId: group.id, keeps: group.symbol })}
                renderWheel={setWheel}
              />
              <span style={{ flex: 1 }} />
              {/* The desktop toolbar's own controls, not copies. */}
              <VisibleBarsBox
                value={visibleBarsOf(group)}
                width={44}
                onChange={(visibleBars) =>
                  dispatch({ type: "setBarWindow", chartGroupId: group.id, visibleBars })
                }
              />
              <GoToDateBox
                calendar={calendar}
                icon
                noteAbove
                onGo={(date) =>
                  setGoTo((prev) => ({ chartGroupId: group.id, date, nonce: (prev?.nonce ?? 0) + 1 }))
                }
              />
              <IntervalPicker
                value={group.frequency}
                compact
                onChange={(frequency) =>
                  dispatch({ type: "setFrequency", chartGroupId: group.id, frequency })
                }
              />
              {/* The desktop's indicator panel as a full-screen sheet, On chart
                  and Add as tabs. Wired as the desktop strip wires it. */}
              <IndicatorButton
                group={group}
                mintId={mintUlid}
                onPanes={(panes) => dispatch({ type: "setPanes", chartGroupId: group.id, panes })}
                onScale={(paneId, scale) =>
                  dispatch({ type: "setPaneScale", chartGroupId: group.id, paneId, scale })
                }
                onPlot={(plotId, patch) => {
                  if (patch.type) dispatch({ type: "setChartType", plotId, chartType: patch.type });
                  if (patch.style) dispatch({ type: "setPlotStyle", plotId, style: patch.style });
                  if (patch.hidden !== undefined) {
                    dispatch({ type: "setPlotHidden", plotId, hidden: patch.hidden });
                  }
                  if (patch.palette) {
                    dispatch({ type: "setPlotPalette", plotId, palette: patch.palette });
                  }
                }}
                sheet={{
                  trigger: (open, toggle) => (
                    <button
                      type="button"
                      aria-label="Indicators"
                      aria-pressed={open}
                      onClick={toggle}
                      style={{ ...KEY, fontStyle: "italic" }}
                    >
                      fx
                    </button>
                  ),
                }}
              />
              <button
                type="button"
                aria-label="Layouts"
                aria-expanded={layoutsOpen}
                onClick={() => setLayoutsOpen((v) => !v)}
                style={KEY}
              >
                ⋯
              </button>
              {/* All ⇄ Full, on a tablet only: ⛶ opens the selected chart alone,
                  ▦ puts every chart back on screen. */}
              {tablet && (
                <button
                  type="button"
                  aria-label={showAll ? "Full chart" : "All charts"}
                  onClick={() => changeChartMode(showAll ? "full" : "all")}
                  style={KEY}
                >
                  {showAll ? "⛶" : "▦"}
                </button>
              )}
              {canStep(groups.length) && (
                <button type="button" aria-label="Next chart" onClick={() => step(1)} style={ARROW}>
                  ›
                </button>
              )}
            </div>
          )}
        </div>

        {layoutsOpen && (
          <LayoutList
            layouts={workspace.layouts}
            current={index}
            onPick={(next) => {
              setLayoutsOpen(false);
              if (next !== index) {
                setLayoutIndex(next);
                // A different layout is a different set of charts.
                setChartIndex(0);
              }
            }}
            onClose={() => setLayoutsOpen(false)}
          />
        )}

        {/* ── Data ──────────────────────────────────────────────────────── */}
        {/* The desktop data panel's views for the chart on screen. A peer
            picked here re-points that chart, as it does on the desktop. */}
        {BRAND.enabled("data") && (
        <div style={{ ...viewStyle(tab === "data"), overflow: "auto" }}>
          {group?.symbol ? (
            <SymbolDataTabs
              symbol={group.symbol}
              tab={dataTab}
              onTab={setDataTab}
              chartGroupId={group.id}
              onSymbol={(symbol) => setSymbol(group.id, symbol)}
              // A phone swipes the views as cards; an iPad keeps the tabs.
              cards={tablet ? undefined : PHONE_CARDS}
            />
          ) : (
            <Notice>No chart to describe.</Notice>
          )}
        </div>
        )}

        {/* ── Journal ───────────────────────────────────────────────────── */}
        {/* Two gates, and they are different questions — the brand decides
            whether this app has a journal at all, `journalEnabled()` whether
            the deployment has a server to keep one on. The first is absence,
            the second is a notice; see `lib/brand-registry.ts`. */}
        {BRAND.enabled("journal") && (
        <div style={viewStyle(tab === "journal")}>
          {journalEnabled() ? (
            <TradesView
              plans={journal.plans}
              trades={journal.trades}
              executions={journal.executions}
              prices={prices}
              active={tradeGroup}
              onPick={setTradeGroup}
              onSelectSymbol={
                group
                  ? (symbol) => {
                      setSymbol(group.id, symbol);
                      setTab("charts");
                    }
                  : undefined
              }
            />
          ) : (
            <Notice>No journal server is configured.</Notice>
          )}
        </div>
        )}

        {/* ── Community ─────────────────────────────────────────────────── */}
        {/* A tab (owner, 2026-09-29). The same panel the desktop docks; on a
            phone a chart opened from a post lands on the Charts tab. */}
        {BRAND.enabled("community") && (
        <div style={viewStyle(tab === "community")}>
          <CommunityPanel
            attach={{ chart: group, layout }}
            onOpenSymbol={(symbol) => {
              if (group) setSymbol(group.id, symbol);
              setTab("charts");
            }}
            onOpenLayout={(definition, name) => {
              dispatch({ type: "addLayout", layout: adoptLayout(definition, name) });
              setLayoutIndex(workspace.layouts.length);
              setChartIndex(0);
              setTab("charts");
            }}
          />
        </div>
        )}

        {/* ── AI ────────────────────────────────────────────────────────── */}
        {/* Reached from More, full screen; ✕ goes back there. */}
        {chatOn && aiOpened && (
          <div style={viewStyle(tab === "agent")}>
            <ChatPanel
              tools={() => clientTools(actionContextRef.current)}
              toolTitle={(name) => actionById(name)?.title}
              context={() => workspaceContext(layout, actionContextRef.current.activeChartGroupId)}
              onClose={() => setTab("more")}
            />
          </div>
        )}

        {/* ── More ──────────────────────────────────────────────────────── */}
        <div style={viewStyle(tab === "more")}>
          <MobileMore
            onOpenAi={chatOn ? () => setTab("agent") : undefined}
            workspace={workspace}
            layoutIndex={index}
            onSelectLayout={(next) => {
              setLayoutIndex(next);
              // A different layout is a different set of charts, so the pager's
              // position means nothing across the change.
              setChartIndex(0);
              setTab("charts");
            }}
            themes={themes}
            onOpenTheme={() => setThemeOpen(true)}
            chartCount={groups.length}
          />
        </div>
      </div>

      <MobileTabBar
        active={tab}
        onSelect={setTab}
        onCycle={cycle}
        chartCount={groups.length}
        chartIndex={chartAt}
        rail={landscape}
      />

      {search !== null && (
        <SymbolDialog
          initialQuery={search.query ?? ""}
          keeps={search.keeps}
          // Nothing to select: the box opens empty here, because a tap on the
          // grip carries no letter the way typing at the desktop does.
          selectSeed={false}
          onPick={(symbol) => {
            setSymbol(search.chartGroupId, symbol);
            setSearch(null);
            // From the screener's "search all symbols": the pick is charted, so
            // go to it, as tapping a row does.
            setTab("charts");
          }}
          onClose={() => setSearch(null)}
        />
      )}

      {themeOpen && (
        <ThemeEditor
          activeThemeRef={workspace.themeRef}
          onSelect={(themeRef) => dispatch({ type: "setTheme", themeRef })}
          themes={themes}
          onSaveTheme={onSaveTheme}
          onClose={() => setThemeOpen(false)}
        />
      )}
    </div>
  );
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        textAlign: "center",
        color: "var(--qf-text-secondary)",
        fontSize: "0.8rem",
      }}
    >
      {children}
    </div>
  );
}
